import { Col, Radio, Row } from 'antd';

import {
  PropertyBoolean as PropertyBooleanOverlay,
  PropertyDatetime as PropertyDatetimeOverlay,
  PropertyEnum,
  PropertyInfo,
  PropertyLabel,
  PropertyList as PropertyListOverlay,
  PropertyNumber as PropertyNumberOverlay,
  PropertyText as PropertyTextOverlay,
} from 'pages/product-full-view/shared/components';

import { OverlayValueWrapper } from './ProductOverlayPropertyItem';

import { findProductPropertyEnums } from '../utils';

import {
  formatSpecialEnum,
  getDataByFieldFullPath,
  mappingSpecialEnumToDisplay,
} from 'pages/product-full-view/shared/utils';

import { filterInvalidValues, transformDataProductOverlay } from './utils';

import { SPECIAL_ENUMS } from 'pages/product-full-view/constants';
import { DATE_FORMAT, NUMERIC_TYPE } from 'static/Constants';

export const ProductOverlayPrimitiveValue = (props) => {
  const { property, productEnums, dataProductOverlay, dataOriginalProduct } =
    props;

  const {
    listName,
    isArray,
    fieldFullPath,
    propertyDisplayName,
    propertyType,
  } = property;

  const isEnumType = Boolean(listName);
  const isListType = Boolean(isArray);

  if (isEnumType) {
    const propertyEnums = findProductPropertyEnums(listName, productEnums);
    const isSpecialEnum = SPECIAL_ENUMS.includes(listName);

    if (isListType) {
      const valueOverlay = filterInvalidValues(
        dataProductOverlay?.[fieldFullPath] ?? []
      );
      const originalValue = filterInvalidValues(
        dataOriginalProduct?.[fieldFullPath] ?? []
      );

      if (valueOverlay.length === 0) return null;

      // Special fields for Allergen
      if (isSpecialEnum) {
        const mappedValuesOverlay = mappingSpecialEnumToDisplay(
          valueOverlay,
          propertyEnums
        );

        const mappedValuesOriginal = mappingSpecialEnumToDisplay(
          originalValue,
          propertyEnums
        );

        return (
          <OverlayValueWrapper
            property={property}
            contentOrigin={
              <PropertyEnum
                propertyDisplayName={propertyDisplayName}
                value={mappedValuesOriginal.join(', ') ?? 'n/a'}
              />
            }
            originalValue={mappedValuesOriginal}
            valueOverlay={mappedValuesOverlay}
          >
            <PropertyEnum
              propertyDisplayName={propertyDisplayName}
              value={mappedValuesOverlay.join(', ')}
            />
          </OverlayValueWrapper>
        );
      }

      return (
        <OverlayValueWrapper
          property={property}
          contentOrigin={
            <PropertyEnum
              propertyDisplayName={propertyDisplayName}
              value={
                originalValue?.length > 0 ? originalValue.join(', ') : 'n/a'
              }
            />
          }
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyEnum
            propertyDisplayName={propertyDisplayName}
            value={valueOverlay.join(', ')}
          />
        </OverlayValueWrapper>
      );
    } else {
      const valueOverlay = dataProductOverlay?.[fieldFullPath];
      const originalValue = dataOriginalProduct?.[fieldFullPath];

      if (!valueOverlay) return null;

      if (isSpecialEnum) {
        const mappedValueOverlay = propertyEnums.find(
          (enumVal) => enumVal.enumCode === valueOverlay
        );

        const mappedOriginalValue = propertyEnums.find(
          (enumVal) => enumVal.enumCode === originalValue
        );

        return (
          <OverlayValueWrapper
            property={property}
            contentOrigin={
              <PropertyEnum
                propertyDisplayName={propertyDisplayName}
                value={
                  originalValue ? formatSpecialEnum(mappedOriginalValue) : 'n/a'
                }
              />
            }
            originalValue={formatSpecialEnum(mappedOriginalValue)}
            valueOverlay={formatSpecialEnum(mappedValueOverlay)}
          >
            <PropertyEnum
              propertyDisplayName={propertyDisplayName}
              value={formatSpecialEnum(mappedValueOverlay)}
            />
          </OverlayValueWrapper>
        );
      }

      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyEnum
              propertyDisplayName={propertyDisplayName}
              value={originalValue ?? 'n/a'}
            />
          }
          property={property}
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyEnum
            propertyDisplayName={propertyDisplayName}
            value={valueOverlay}
          />
        </OverlayValueWrapper>
      );
    }
  } else {
    const originalValue = getDataByFieldFullPath(
      dataOriginalProduct,
      fieldFullPath
    );

    const valueOverlay = getDataByFieldFullPath(
      dataProductOverlay,
      fieldFullPath
    );

    if (isListType) {
      const filteredOriginalValue = filterInvalidValues(originalValue ?? []);
      const filteredValueOverlay = filterInvalidValues(valueOverlay ?? []);

      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyListOrigin
              property={property}
              data={transformDataProductOverlay({
                dataProduct: dataOriginalProduct,
                fieldFullPath,
              })}
            />
          }
          property={property}
          originalValue={filteredOriginalValue}
          valueOverlay={filteredValueOverlay}
        >
          <PropertyListOverlay property={property} data={dataProductOverlay} />
        </OverlayValueWrapper>
      );
    }

    if (propertyType === 'string') {
      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyTextOrigin
              property={property}
              data={transformDataProductOverlay({
                dataProduct: dataOriginalProduct,
                fieldFullPath,
              })}
            />
          }
          property={property}
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyTextOverlay property={property} data={dataProductOverlay} />
        </OverlayValueWrapper>
      );
    }

    if (NUMERIC_TYPE.includes(propertyType)) {
      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyNumberOrigin
              property={property}
              data={transformDataProductOverlay({
                dataProduct: dataOriginalProduct,
                fieldFullPath,
              })}
            />
          }
          property={property}
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyNumberOverlay
            property={property}
            data={dataProductOverlay}
          />
        </OverlayValueWrapper>
      );
    }

    if (propertyType === 'datetime') {
      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyDatetimeOrigin
              property={property}
              data={transformDataProductOverlay({
                dataProduct: dataOriginalProduct,
                fieldFullPath,
                isDatetime: true,
              })}
            />
          }
          property={property}
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyDatetimeOverlay
            property={property}
            data={dataProductOverlay}
          />
        </OverlayValueWrapper>
      );
    }

    if (propertyType === 'boolean') {
      return (
        <OverlayValueWrapper
          contentOrigin={
            <PropertyBooleanOrigin
              property={property}
              data={transformDataProductOverlay({
                dataProduct: dataOriginalProduct,
                fieldFullPath,
              })}
            />
          }
          property={property}
          originalValue={originalValue}
          valueOverlay={valueOverlay}
        >
          <PropertyBooleanOverlay
            property={property}
            data={dataProductOverlay}
          />
        </OverlayValueWrapper>
      );
    }

    return (
      <OverlayValueWrapper
        contentOrigin={
          <PropertyTextOrigin
            property={property}
            data={transformDataProductOverlay({
              dataProduct: dataOriginalProduct,
              fieldFullPath,
            })}
          />
        }
        property={property}
        originalValue={originalValue}
        valueOverlay={valueOverlay}
      >
        <PropertyTextOverlay property={property} data={dataProductOverlay} />
      </OverlayValueWrapper>
    );
  }
};

const PropertyListOrigin = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName, propertyType } = property;

  const values = getDataByFieldFullPath(data, fieldFullPath) ?? [];

  let filteredValues = filterInvalidValues(values);

  filteredValues = filteredValues.map((item) =>
    propertyType === 'datetime' ? item.format(DATE_FORMAT.SYSTEM_FORMAT) : item
  );

  const isEmptyValues = filteredValues?.length === 0;

  return (
    <Row>
      <Col span={12}>
        <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
      </Col>
      <Col span={12}>
        <PropertyInfo>
          {isEmptyValues ? 'n/a' : filteredValues.join(', ')}
        </PropertyInfo>
      </Col>
    </Row>
  );
};

export const PropertyDatetimeOrigin = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <Row>
      <Col span={12}>
        <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
      </Col>
      <Col span={12}>
        <PropertyInfo>
          {value ? value?.format(DATE_FORMAT.SYSTEM_FORMAT) : 'n/a'}
        </PropertyInfo>
      </Col>
    </Row>
  );
};

export const PropertyNumberOrigin = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <Row>
      <Col span={12}>
        <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
      </Col>
      <Col span={12}>
        <PropertyInfo>{value ? value : 'n/a'}</PropertyInfo>
      </Col>
    </Row>
  );
};

export const PropertyBooleanOrigin = ({ property, data }) => {
  const { propertyDisplayName, fieldFullPath } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  const isBooleanType = typeof value === 'boolean';

  return (
    <Row>
      <Col span={12}>
        <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
      </Col>
      <Col span={12}>
        {isBooleanType ? (
          <Radio.Group value={value}>
            <Radio style={{ marginLeft: 8 }} value={true}>
              True
            </Radio>
            <Radio style={{ marginLeft: 8 }} value={false}>
              False
            </Radio>
          </Radio.Group>
        ) : (
          <PropertyInfo>{value}</PropertyInfo>
        )}
      </Col>
    </Row>
  );
};

export const PropertyTextOrigin = ({ property, data }) => {
  const { fieldFullPath, propertyDisplayName } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);

  return (
    <Row>
      <Col span={12}>
        <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
      </Col>
      <Col span={12}>
        <PropertyInfo>{value ? value : 'n/a'}</PropertyInfo>
      </Col>
    </Row>
  );
};
